/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Tag, AlertTriangle, Truck, FileText, Search, Trash2 } from "lucide-react";
import ColumnChooser from "@/components/ColumnChooser";
import { useColumnPrefs } from "@/hooks/useColumnPrefs";
import { useAppSettings } from "@/hooks/useAppSettings";

const PRICING_COLUMN_KEYS = ["MSRP", "Disc", "Reseller_Price", "Line_Total"];

export default function PortalInventoryPage() {
  const [searchParams] = useSearchParams();
  const asCompany = searchParams.get("as_company");
  const apiParams = asCompany ? { as_company: asCompany } : {};
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState({});

  // Admin-controlled — resellers cannot toggle this themselves.
  const settings = useAppSettings();
  const showPricing = !!settings?.Portal_Show_Pricing;

  const [postcode, setPostcode] = useState("");
  const [zone, setZone] = useState(null);
  const [zoneCost, setZoneCost] = useState(0);
  const [zoneError, setZoneError] = useState("");

  useEffect(() => {
    Promise.all([api.get("/portal/inventory", { params: apiParams }), api.get("/inventory")])
      .then(([r, p]) => { setRows(r.data); setProducts(p.data); })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  const stockOf = (pid) => {
    const it = products.find((p) => p.id === pid);
    if (!it) return null;
    return Math.max(0, (it.Total_Stock || 0) - (it.Allocated_Stock || 0));
  };
  const minOf = (pid) => products.find((p) => p.id === pid)?.Min_Stock_Warning ?? 100;

  const setQty = (pid, v) => {
    const n = Math.max(0, Number(v) || 0);
    setCart((c) => {
      const next = { ...c };
      if (n === 0) delete next[pid]; else next[pid] = n;
      return next;
    });
  };

  const filtered = useMemo(() => {
    const term = q.toLowerCase();
    return rows.filter((r) => !term ||
      r.Product_Name.toLowerCase().includes(term) ||
      (r.SKU || "").toLowerCase().includes(term) ||
      (r.Colour || "").toLowerCase().includes(term));
  }, [rows, q]);

  const totals = useMemo(() => {
    let subtotal = 0, weight = 0, pallets = 0;
    const lines = [];
    for (const [pid, qty] of Object.entries(cart)) {
      const r = rows.find((x) => x.Product_ID === pid);
      if (!r || !qty) continue;
      const lineTotal = (r.Reseller_Price || 0) * qty;
      const lineWeight = (r.Unit_Weight_KG || 0) * qty;
      const linePallets = r.Units_Per_Pallet ? Math.ceil(qty / r.Units_Per_Pallet) : 0;
      subtotal += lineTotal; weight += lineWeight; pallets += linePallets;
      lines.push({ ...r, qty, lineTotal, lineWeight, linePallets });
    }
    return { lines, subtotal, weight, pallets };
  }, [cart, rows]);

  const checkPostcode = async (e) => {
    e?.preventDefault();
    setZone(null); setZoneError("");
    if (!postcode) return;
    try {
      const { data } = await api.get(`/delivery-zones/lookup`, { params: { postcode: Number(postcode), pallets: totals.pallets } });
      if (data.matched) { setZone(data.zone); setZoneCost(data.cost); }
      else setZoneError(`No delivery zone covers postcode ${postcode}. Please contact us.`);
    } catch (e) { setZoneError(e.message); }
  };

  const delivery = zone ? zoneCost : 0;
  const total = totals.subtotal + delivery;

  // Re-lookup whenever pallet count changes so per-pallet pricing stays accurate
  useEffect(() => {
    if (!zone) return;
    api.get(`/delivery-zones/lookup`, { params: { postcode: Number(postcode), pallets: totals.pallets } })
      .then(({ data }) => { if (data.matched) setZoneCost(data.cost); })
      .catch(() => {});
  }, [totals.pallets]); // eslint-disable-line react-hooks/exhaustive-deps

  const allColumns = [
    {
      key: "Product", label: "Product",
      cell: (r) => (
        <>
          <div className="font-semibold text-zinc-900">{r.Product_Name}</div>
          {r.Colour && <div className="text-xs text-zinc-500">{r.Colour}</div>}
        </>
      ),
    },
    { key: "SKU", label: "SKU", cell: (r) => <span className="font-mono text-xs">{r.SKU || "—"}</span> },
    {
      key: "Stock", label: "Stock", align: "center",
      cell: (r) => {
        const stock = stockOf(r.Product_ID);
        const minW = minOf(r.Product_ID);
        const low = stock != null && stock < minW;
        return stock != null ? (
          <span className={`text-xs font-semibold inline-flex items-center justify-center gap-1 ${low ? "text-red-600" : "text-green-700"}`}>
            {low && <AlertTriangle className="w-3 h-3" />}{stock.toLocaleString()}
          </span>
        ) : <span>—</span>;
      },
    },
    { key: "MSRP", label: "MSRP", align: "center", cell: (r) => <span className="font-mono text-zinc-500 line-through">${(r.MSRP || 0).toFixed(2)}</span> },
    { key: "Disc", label: "Disc", align: "center", cell: (r) => <span className="font-mono text-xs">{r.Discount_Percent || 0}%</span> },
    { key: "Reseller_Price", label: "Your price", align: "center", cell: (r) => <span className="font-mono font-bold text-brand-700">${(r.Reseller_Price || 0).toFixed(2)}</span> },
    { key: "Pallet_Weight", label: "Pallet / kg", align: "center", defaultVisible: false, cell: (r) => <span className="text-[11px] text-zinc-500 font-mono">{r.Units_Per_Pallet ? `${r.Units_Per_Pallet} / ${r.Unit_Weight_KG || 0}kg` : "—"}</span> },
    {
      key: "Qty", label: "Qty", locked: true, align: "center", width: "w-32",
      cell: (r) => {
        const qty = cart[r.Product_ID] || "";
        return (
          <input
            data-testid={`portal-qty-${r.Product_ID}`}
            type="number"
            min="0"
            value={qty}
            onChange={(e) => setQty(r.Product_ID, e.target.value)}
            placeholder="0"
            className="es-input w-24 text-center font-mono mx-auto"
          />
        );
      },
    },
    {
      key: "Line_Total", label: "Line total", align: "center",
      cell: (r) => {
        const n = Number(cart[r.Product_ID]) || 0;
        const lineTotal = n * (r.Reseller_Price || 0);
        return <span className="font-mono font-semibold">{n > 0 ? `$${lineTotal.toFixed(2)}` : ""}</span>;
      },
    },
  ];

  const { visibleColumns: rawVisible, orderedColumns, isVisible, toggleColumn, moveColumn, reset } =
    useColumnPrefs("portal_inventory", allColumns);

  // When pricing toggle is OFF we hide every pricing-related column (and the
  // line-total which is derived from price). The Qty column is also irrelevant
  // when there's no price to multiply, so we drop it too. The full cart /
  // delivery sidebar is hidden lower down.
  const visibleColumns = useMemo(() => (
    showPricing ? rawVisible : rawVisible.filter((c) => !PRICING_COLUMN_KEYS.includes(c.key) && c.key !== "Qty")
  ), [rawVisible, showPricing]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Inventory{showPricing ? " & Pricing" : ""}</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {showPricing
              ? "Browse products + build a quote reference for your own purchase order."
              : "Browse live stock levels across every product."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Order: Search → Columns. Pricing visibility is controlled by admin in Settings → Reseller Portal. */}
          <div className="relative w-64">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="portal-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="es-input pl-9" />
          </div>
          <ColumnChooser
            tableLabel="portal_inventory"
            orderedColumns={orderedColumns}
            isVisible={isVisible}
            toggleColumn={toggleColumn}
            moveColumn={moveColumn}
            reset={reset}
          />
        </div>
      </div>

      {showPricing && (
        <div className="es-card p-3 flex items-center gap-3 text-sm text-zinc-600">
          <Tag className="w-4 h-4 text-brand-600 flex-shrink-0" />
          <span>Your_Price = MSRP × (1 − your discount). Delivery cost calculated by postcode. <strong>Quotes are reference-only</strong> — use them when raising your PO with Edenstone.</span>
        </div>
      )}

      {err && <div className="es-card p-4 text-sm text-red-700">{err}</div>}

      <div
        className={
          showPricing
            ? "grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 items-start"
            : "grid grid-cols-1 gap-4 items-start"
        }
      >
        {/* Products TABLE */}
        <div className="es-card overflow-x-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-zinc-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-zinc-500">No products found.</div>
          ) : (
            <table className="es-table">
              <thead>
                <tr>
                  {visibleColumns.map((c) => (
                    <th key={c.key} className={[c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "", c.width || ""].join(" ")}>
                      {c.header ? c.header() : c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.Product_ID} data-testid={`portal-inv-${r.Product_ID}`}>
                    {visibleColumns.map((c) => (
                      <td key={c.key} className={c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : ""}>{c.cell(r)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Sticky sidebar — delivery + cart — hidden when pricing toggle is off */}
        {showPricing && (
        <aside className="lg:sticky lg:top-4 space-y-3" data-testid="portal-cart-sidebar">
          <div className="es-card p-4">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5 text-brand-600" /> Delivery Zone
            </div>
            <form onSubmit={checkPostcode} className="flex items-center gap-2">
              <input
                data-testid="portal-postcode"
                type="text" inputMode="numeric"
                value={postcode} onChange={(e) => setPostcode(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
                placeholder="Postcode"
                className="es-input flex-1 font-mono"
              />
              <button data-testid="portal-check-postcode" className="es-btn-secondary">Check</button>
            </form>
            {zone && (
              <div className="mt-2 text-sm">
                <div className="font-semibold text-zinc-900">{zone.Zone_Name}</div>
                <div className="text-xs text-zinc-500">{zone.Min_Postcode} – {zone.Max_Postcode}</div>
                <div className="mt-1 font-mono text-brand-700 font-bold">${zoneCost.toFixed(2)}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  ${(zone.Cost_AUD || 0).toFixed(2)} base
                  {zone.Cost_Per_Pallet > 0 && <> + ${zone.Cost_Per_Pallet.toFixed(2)} × {totals.pallets} pallet{totals.pallets === 1 ? "" : "s"}</>}
                </div>
              </div>
            )}
            {zoneError && <div className="mt-2 text-xs text-amber-700">{zoneError}</div>}
          </div>

          <div className="es-card">
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-brand-600" /> Selected Items
              </div>
              <span className="text-[10px] text-zinc-400">{totals.lines.length} line{totals.lines.length === 1 ? "" : "s"}</span>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {totals.lines.length === 0 ? (
                <div className="text-center text-xs text-zinc-400 py-8 px-4">Enter a quantity in the table to add an item.</div>
              ) : totals.lines.map((l) => (
                <div key={l.Product_ID} data-testid={`portal-cart-${l.Product_ID}`} className="px-4 py-2.5 border-b border-zinc-50 last:border-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{l.Product_Name}</div>
                      <div className="text-[11px] text-zinc-500 font-mono">{l.qty} × ${(l.Reseller_Price || 0).toFixed(2)} · {l.linePallets} pallet{l.linePallets === 1 ? "" : "s"}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-mono font-semibold">${l.lineTotal.toFixed(2)}</div>
                      <button onClick={() => setQty(l.Product_ID, 0)} className="text-zinc-400 hover:text-red-600 mt-0.5"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {totals.lines.length > 0 && (
              <div className="border-t border-zinc-100 px-4 py-3 space-y-1.5 text-sm">
                <Row label="Subtotal" value={`$${totals.subtotal.toFixed(2)}`} />
                <Row label={`Delivery${zone ? "" : " (no postcode)"}`} value={`$${delivery.toFixed(2)}`} muted={!zone} />
                <Row label="Pallets / weight" value={`${totals.pallets} · ${totals.weight.toFixed(0)} kg`} small />
                <div className="border-t border-zinc-100 pt-2 mt-1 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-zinc-500">Total</span>
                  <span data-testid="portal-cart-total" className="font-mono text-lg font-bold text-brand-700">${total.toFixed(2)}</span>
                </div>
                <div className="text-[10px] text-zinc-400 italic pt-1">Ready to order? Contact Edenstone with this quote.</div>
              </div>
            )}
          </div>
        </aside>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, muted, small }) {
  return (
    <div className={`flex items-center justify-between ${small ? "text-xs" : ""} ${muted ? "text-zinc-400" : "text-zinc-700"}`}>
      <span>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
